/*@flow*/
import type { KeyedCollection } from 'immutable';
import getIn from 'lib/getIn';
import hasProperty from 'lib/hasProperty';

// Types
type PathT = string | Function;
type PathMapT = { [key: string]: PathT } | Function;
type OptsT = {
  ownProps?: Object,
};
type StateT = KeyedCollection<*, *>;

// remap creates a domain specific language for accessing immutable Maps in a redux
// store. It does that by extracting a deeply nested property and maps it to a key, and
// returning a `mapStateToProps` function because it's intended for use in react-redux's
// `connect`.
//
//
// Usage:
//
//    connect(remap({
//      todos: 'app.todos',
//      isHidden: () => state('user.role') !== 'admin'
//    }))
//    (class TodoItems extends React.Component {
//      render() {
//        if (this.props.isHidden)
//          return null
//        return this.props.todos.map(o => <li>{o}</li>)
//      }
//    })
//
export default function remap(pathMap: PathMapT = {}, opts?: OptsT): Function {
  let ownProps = (opts && opts.ownProps) || {};

  // getProp splits the value string for the key k at the field separator, i.e. period,
  // and uses the array to get the value in the state reducer
  const getProp = (k: string, state: StateT): any => {
    let value = pathMap[k];

    const isPromise =
      typeof value === 'object' &&
      value.then &&
      typeof value.then === 'function';

    if (typeof value === 'function') {
      return value(ownProps);
    } else if (typeof value === 'string') {
      return getIn(() => state)(value, undefined);
    } else if (isPromise) {
      return Promise.resolve(value);
    } else if (Array.isArray(value)) {
      return getIn(() => state)(value, undefined);
    }
    throw new Error(
      '[remap] value type `' + typeof value + '` cannot be handled.'
    );
  };

  // mapStateToProps maps the property at the path in the value of pathMap
  // to a prop at the key of the pathMap
  return function mapStateToProps(state: StateT): Object {
    return Object.keys(pathMap).reduce((s, k) => {
      let value = hasProperty(ownProps, k) ? ownProps[k] : getProp(k, state);
      return { ...s, [k]: value };
    }, {});
  };
}
